import type { PrescriptionDeepState } from './prescriptionState';

/**
 * Pathology-based prescription presets.
 *
 * Each entry maps a common foot pathology to a partial PrescriptionDeepState
 * (just the modification sections: rearfoot / midfoot / forefoot /
 * offloadings). Applying one merges these into the current prescription
 * without touching the insole design or the patient.
 *
 * These mappings are clinically defined per the operator-provided spec
 * (no longer placeholder). They remain editable in this file so the
 * clinical team can revise them with a single edit, the rest of the UI
 * picks up changes automatically.
 *
 * A sibling array `SANDAL_PATHOLOGY_PRESETS` mirrors the same 8 entries
 * with sandal-form-friendly defaults: intrinsic-only modifications where
 * the sandal form hides the extrinsic/hardness controls, no medial /
 * lateral flange (the sandal form does not render flanges), and the
 * heel-spur preset uses heel_offloading instead of heel_cutout. Use the
 * `getPathologyPresets(isSandal)` helper to pick the right array.
 */

export interface PathologyPreset {
  id: string;
  label: string;
  /** One-line clinical hint shown under the label. */
  hint: string;
  /** Partial deep state applied (merged) when selected. */
  apply: PrescriptionDeepState;
}

export const PATHOLOGY_PRESETS: ReadonlyArray<PathologyPreset> = [
  {
    id: 'plantar_fasciitis_neutral',
    label: 'Plantar fasciitis (neutral or supinated)',
    hint: 'Heel offloading + extrinsic heel raise + fascia accommodation + pronating forefoot wedge.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        heel_raise: {
          left_type: 'extrinsic',
          left_length: 'short',
          left_height_mm: 5,
          right_type: 'extrinsic',
          right_length: 'short',
          right_height_mm: 5,
        },
      },
      midfoot: {
        fascial_accommodation: {
          left: 'yes_poron',
          left_depth_mm: -3,
          right: 'yes_poron',
          right_depth_mm: -3,
        },
      },
      forefoot: {
        forefoot_wedge: {
          left_type: 'extrinsic',
          left_correction: 'pronating',
          left_height_mm: 3,
          left_hardness: 'shore_a_35',
          right_type: 'extrinsic',
          right_correction: 'pronating',
          right_height_mm: 3,
          right_hardness: 'shore_a_35',
        },
      },
    },
  },
  {
    id: 'plantar_fasciitis_pronated',
    label: 'Plantar fasciitis (pronated)',
    hint: 'Heel offloading + heel raise + medial Kirby skive + fascia accommodation + supinating forefoot wedge.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        heel_raise: {
          left_type: 'extrinsic',
          left_length: 'short',
          left_height_mm: 3,
          right_type: 'extrinsic',
          right_length: 'short',
          right_height_mm: 3,
        },
        rearfoot_wedge: {
          left_type: 'kirby_skive',
          left_correction: 'medial',
          left_degrees: 15,
          left_depth_mm: -3,
          right_type: 'kirby_skive',
          right_correction: 'medial',
          right_degrees: 15,
          right_depth_mm: -3,
        },
      },
      midfoot: {
        fascial_accommodation: {
          left: 'yes_poron',
          left_depth_mm: -3,
          right: 'yes_poron',
          right_depth_mm: -3,
        },
      },
      forefoot: {
        forefoot_wedge: {
          left_type: 'extrinsic',
          left_correction: 'supinating',
          left_height_mm: 3,
          left_hardness: 'shore_a_35',
          right_type: 'extrinsic',
          right_correction: 'supinating',
          right_height_mm: 3,
          right_hardness: 'shore_a_35',
        },
      },
    },
  },
  {
    id: 'functional_hallux_limitus',
    label: 'Functional hallux limitus (pronated)',
    hint: 'Heel offloading + medial Kirby skive + semi-rigid intrinsic Morton extension.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        rearfoot_wedge: {
          left_type: 'kirby_skive',
          left_correction: 'medial',
          left_degrees: 15,
          left_depth_mm: -3,
          right_type: 'kirby_skive',
          right_correction: 'medial',
          right_degrees: 15,
          right_depth_mm: -3,
        },
      },
      forefoot: {
        morton_extension: {
          left_type: 'intrinsic',
          left_thickness: '2mm',
          left_softness: 'semi_rigid',
          right_type: 'intrinsic',
          right_thickness: '2mm',
          right_softness: 'semi_rigid',
        },
      },
    },
  },
  {
    id: 'pes_planus',
    label: 'Pes planus (flatfoot)',
    hint: 'Heel offloading + medial Kirby skive + MLA extra fill + medial flange.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        rearfoot_wedge: {
          left_type: 'kirby_skive',
          left_correction: 'medial',
          left_degrees: 15,
          left_depth_mm: -2,
          right_type: 'kirby_skive',
          right_correction: 'medial',
          right_degrees: 15,
          right_depth_mm: -2,
        },
      },
      midfoot: {
        mla_extra_filled: { left: 'poron_3mm_12deg', right: 'poron_3mm_12deg' },
        medial_flange: { left_enabled: true, right_enabled: true },
      },
    },
  },
  {
    id: 'pediatric_pes_planus',
    label: 'Pediatric pes planus',
    hint: 'Heel offloading + heel raise + medial Kirby skive + MLA extra fill + medial flange.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        heel_raise: {
          left_type: 'extrinsic',
          left_length: 'short',
          left_height_mm: 3,
          right_type: 'extrinsic',
          right_length: 'short',
          right_height_mm: 3,
        },
        rearfoot_wedge: {
          left_type: 'kirby_skive',
          left_correction: 'medial',
          left_degrees: 15,
          left_depth_mm: -2,
          right_type: 'kirby_skive',
          right_correction: 'medial',
          right_degrees: 15,
          right_depth_mm: -2,
        },
      },
      midfoot: {
        mla_extra_filled: { left: 'poron_3mm_12deg', right: 'poron_3mm_12deg' },
        medial_flange: { left_enabled: true, right_enabled: true },
      },
    },
  },
  {
    id: 'pes_cavus',
    label: 'Pes cavus (high arch)',
    hint: 'Heel offloading + lateral Kirby skive + lateral flange + pronating forefoot wedge + heel cushion.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        rearfoot_wedge: {
          left_type: 'kirby_skive',
          left_correction: 'lateral',
          left_degrees: 15,
          left_depth_mm: -2,
          right_type: 'kirby_skive',
          right_correction: 'lateral',
          right_degrees: 15,
          right_depth_mm: -2,
        },
      },
      midfoot: {
        lateral_flange: { left_enabled: true, right_enabled: true },
      },
      forefoot: {
        forefoot_wedge: {
          left_type: 'extrinsic',
          left_correction: 'pronating',
          left_height_mm: 3,
          left_hardness: 'shore_a_35',
          right_type: 'extrinsic',
          right_correction: 'pronating',
          right_height_mm: 3,
          right_hardness: 'shore_a_35',
        },
      },
      offloadings: {
        extra_heel_cushion_pad: {
          left: 'poron',
          left_thickness: '3mm',
          right: 'poron',
          right_thickness: '3mm',
        },
      },
    },
  },
  {
    id: 'mth_overload',
    label: 'Metatarsalgia (MTH overload)',
    hint: 'Heel offloading + MTT bar M1-5 + Subcapital bar (poron).',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
      },
      offloadings: {
        mtt_bar: {
          left_location: 'm1_5',
          left_thickness: '3mm',
          right_location: 'm1_5',
          right_thickness: '3mm',
        },
        subcapital_bar: {
          left: 'poron',
          left_thickness: '3mm',
          right: 'poron',
          right_thickness: '3mm',
        },
      },
    },
  },
  {
    id: 'heel_spur',
    label: 'Calcaneal spur (heel spur)',
    hint: 'Dedicated heel cutout + intrinsic heel raise + fascia accommodation + heel cushion. The default subcalcaneus offloading is replaced by the heel cutout for this preset only.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_cutout', fill: 'carbosan_3mm', dimension: 'medium', position: 'center' },
          right: { type: 'heel_cutout', fill: 'carbosan_3mm', dimension: 'medium', position: 'center' },
        },
        heel_raise: {
          left_type: 'intrinsic',
          left_length: 'short',
          left_height_mm: 3,
          right_type: 'intrinsic',
          right_length: 'short',
          right_height_mm: 3,
        },
      },
      midfoot: {
        fascial_accommodation: {
          left: 'yes_poron',
          left_depth_mm: -3,
          right: 'yes_poron',
          right_depth_mm: -3,
        },
      },
      offloadings: {
        extra_heel_cushion_pad: {
          left: 'poron',
          left_thickness: '3mm',
          right: 'poron',
          right_thickness: '3mm',
        },
      },
    },
  },
];

/**
 * Sandal-form variants of the 8 canonical pathology presets. The sandal
 * form hides extrinsic posting controls (no extrinsic length/hardness),
 * medial + lateral flanges, and the heel_cutout subcalcaneal variant.
 * These variants pre-fill with intrinsic equivalents and substitute
 * heel_offloading for heel_cutout so every patch lands on a visible
 * field in the sandal flow.
 */
export const SANDAL_PATHOLOGY_PRESETS: ReadonlyArray<PathologyPreset> = [
  {
    id: 'plantar_fasciitis_neutral',
    label: 'Plantar fasciitis (neutral or supinated)',
    hint: 'Heel offloading + intrinsic heel raise + fascia accommodation + pronating forefoot wedge.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        heel_raise: {
          left_type: 'intrinsic',
          left_length: 'short',
          left_height_mm: 2,
          right_type: 'intrinsic',
          right_length: 'short',
          right_height_mm: 2,
        },
      },
      midfoot: {
        fascial_accommodation: {
          left: 'yes_poron',
          left_depth_mm: -3,
          right: 'yes_poron',
          right_depth_mm: -3,
        },
      },
      forefoot: {
        forefoot_wedge: {
          left_type: 'intrinsic',
          left_correction: 'pronating',
          left_height_mm: 2,
          right_type: 'intrinsic',
          right_correction: 'pronating',
          right_height_mm: 2,
        },
      },
    },
  },
  {
    id: 'plantar_fasciitis_pronated',
    label: 'Plantar fasciitis (pronated)',
    hint: 'Heel offloading + intrinsic heel raise + medial Kirby skive + fascia accommodation + supinating forefoot wedge.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        heel_raise: {
          left_type: 'intrinsic',
          left_length: 'short',
          left_height_mm: 2,
          right_type: 'intrinsic',
          right_length: 'short',
          right_height_mm: 2,
        },
        rearfoot_wedge: {
          left_type: 'kirby_skive',
          left_correction: 'medial',
          left_degrees: 15,
          left_depth_mm: -3,
          right_type: 'kirby_skive',
          right_correction: 'medial',
          right_degrees: 15,
          right_depth_mm: -3,
        },
      },
      midfoot: {
        fascial_accommodation: {
          left: 'yes_poron',
          left_depth_mm: -3,
          right: 'yes_poron',
          right_depth_mm: -3,
        },
      },
      forefoot: {
        forefoot_wedge: {
          left_type: 'intrinsic',
          left_correction: 'supinating',
          left_height_mm: 2,
          right_type: 'intrinsic',
          right_correction: 'supinating',
          right_height_mm: 2,
        },
      },
    },
  },
  {
    id: 'functional_hallux_limitus',
    label: 'Functional hallux limitus (pronated)',
    hint: 'Heel offloading + medial Kirby skive + semi-rigid intrinsic Morton extension.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        rearfoot_wedge: {
          left_type: 'kirby_skive',
          left_correction: 'medial',
          left_degrees: 15,
          left_depth_mm: -3,
          right_type: 'kirby_skive',
          right_correction: 'medial',
          right_degrees: 15,
          right_depth_mm: -3,
        },
      },
      forefoot: {
        morton_extension: {
          left_type: 'intrinsic',
          left_thickness: '2mm',
          left_softness: 'semi_rigid',
          right_type: 'intrinsic',
          right_thickness: '2mm',
          right_softness: 'semi_rigid',
        },
      },
    },
  },
  {
    id: 'pes_planus',
    label: 'Pes planus (flatfoot)',
    hint: 'Heel offloading + medial Kirby skive + MLA extra fill.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        rearfoot_wedge: {
          left_type: 'kirby_skive',
          left_correction: 'medial',
          left_degrees: 15,
          left_depth_mm: -2,
          right_type: 'kirby_skive',
          right_correction: 'medial',
          right_degrees: 15,
          right_depth_mm: -2,
        },
      },
      midfoot: {
        mla_extra_filled: { left: 'poron_3mm_12deg', right: 'poron_3mm_12deg' },
      },
    },
  },
  {
    id: 'pediatric_pes_planus',
    label: 'Pediatric pes planus',
    hint: 'Heel offloading + intrinsic heel raise + medial Kirby skive + MLA extra fill.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        heel_raise: {
          left_type: 'intrinsic',
          left_length: 'short',
          left_height_mm: 2,
          right_type: 'intrinsic',
          right_length: 'short',
          right_height_mm: 2,
        },
        rearfoot_wedge: {
          left_type: 'kirby_skive',
          left_correction: 'medial',
          left_degrees: 15,
          left_depth_mm: -2,
          right_type: 'kirby_skive',
          right_correction: 'medial',
          right_degrees: 15,
          right_depth_mm: -2,
        },
      },
      midfoot: {
        mla_extra_filled: { left: 'poron_3mm_12deg', right: 'poron_3mm_12deg' },
      },
    },
  },
  {
    id: 'pes_cavus',
    label: 'Pes cavus (high arch)',
    hint: 'Heel offloading + lateral Kirby skive + intrinsic pronating forefoot wedge + heel cushion.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        rearfoot_wedge: {
          left_type: 'kirby_skive',
          left_correction: 'lateral',
          left_degrees: 15,
          left_depth_mm: -2,
          right_type: 'kirby_skive',
          right_correction: 'lateral',
          right_degrees: 15,
          right_depth_mm: -2,
        },
      },
      forefoot: {
        forefoot_wedge: {
          left_type: 'intrinsic',
          left_correction: 'pronating',
          left_height_mm: 2,
          right_type: 'intrinsic',
          right_correction: 'pronating',
          right_height_mm: 2,
        },
      },
      offloadings: {
        extra_heel_cushion_pad: {
          left: 'poron',
          left_thickness: '3mm',
          right: 'poron',
          right_thickness: '3mm',
        },
      },
    },
  },
  {
    id: 'mth_overload',
    label: 'Metatarsalgia (MTH overload)',
    hint: 'Heel offloading + MTT bar M1-5 + Subcapital bar (poron).',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
      },
      offloadings: {
        mtt_bar: {
          left_location: 'm1_5',
          left_thickness: '3mm',
          right_location: 'm1_5',
          right_thickness: '3mm',
        },
        subcapital_bar: {
          left: 'poron',
          left_thickness: '3mm',
          right: 'poron',
          right_thickness: '3mm',
        },
      },
    },
  },
  {
    id: 'heel_spur',
    label: 'Calcaneal spur (heel spur)',
    hint: 'Heel offloading + intrinsic heel raise + fascia accommodation + heel cushion.',
    apply: {
      rearfoot: {
        subcalcaneal_offloading: {
          left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
          right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        },
        heel_raise: {
          left_type: 'intrinsic',
          left_length: 'short',
          left_height_mm: 3,
          right_type: 'intrinsic',
          right_length: 'short',
          right_height_mm: 3,
        },
      },
      midfoot: {
        fascial_accommodation: {
          left: 'yes_poron',
          left_depth_mm: -3,
          right: 'yes_poron',
          right_depth_mm: -3,
        },
      },
      offloadings: {
        extra_heel_cushion_pad: {
          left: 'poron',
          left_thickness: '3mm',
          right: 'poron',
          right_thickness: '3mm',
        },
      },
    },
  },
];

/**
 * Pick the right pathology preset array for the current product family.
 * Sandal flows get sandal-form-friendly defaults (intrinsic-only, no
 * flanges, heel_offloading instead of heel_cutout). Everything else
 * gets the canonical insole presets.
 */
export function getPathologyPresets(
  isSandal: boolean,
): ReadonlyArray<PathologyPreset> {
  return isSandal ? SANDAL_PATHOLOGY_PRESETS : PATHOLOGY_PRESETS;
}
